import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import PageLayout from '../../components/PageLayout';
import { getBot, listBotVersions, deleteBot, deleteBotVersion, getBotVersion, createBotVersion, updateBotVersionName, listBacktestRuns, listLiveRuns, stopLiveRun } from '../../api/botsApi';
import { fetchAccounts } from '../../api/api';
import { Button } from '../../components/ui/button'; // Assuming named export
import LaunchLiveRunModal from '../../components/bots/LaunchLiveRunModal';
import LaunchBacktestModal from '../../components/bots/LaunchBacktestModal';
import { Rocket, Pencil, Eye, PlayCircle, Trash2, Copy, StopCircle } from 'lucide-react';
import StatusBadge from '../../components/bots/StatusBadge';

const BotDetailPage = () => {
  const { botId } = useParams();
  const navigate = useNavigate();
  const [bot, setBot] = useState(null);
  const [versions, setVersions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [showLiveModal, setShowLiveModal] = useState(false);
  const [showBacktestModal, setShowBacktestModal] = useState(false);
  const [lastRuns, setLastRuns] = useState({}); // { [versionId]: run }
  const [lastLiveRuns, setLastLiveRuns] = useState({}); // { [versionId]: liveRun }
  const [accounts, setAccounts] = useState([]);
  const [stoppingVersionId, setStoppingVersionId] = useState(null);

  useEffect(() => {
    const fetchBotDetails = async () => {
      setIsLoading(true);
      try {
        const botData = await getBot(botId);
        setBot(botData);
        const versionsData = await listBotVersions(botId);
        // API may return paginated response: { count, next, previous, results }
        const versionsArray = versionsData?.results || versionsData;
        setVersions(Array.isArray(versionsArray) ? versionsArray : []);
        setError(null);
      } catch (err) {
        console.error(`Failed to fetch bot details for ${botId}:`, err);
        setError(err.message || `Failed to fetch details for bot ${botId}.`);
      } finally {
        setIsLoading(false);
      }
    };
    fetchBotDetails();
  }, [botId]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const accs = await fetchAccounts();
        if (!mounted) return;
        setAccounts(Array.isArray(accs?.results) ? accs.results : accs || []);
      } catch (e) {
        // ignore
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Fetch latest backtest run per version for display
  useEffect(() => {
    if (!versions || versions.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const pairs = await Promise.all(
          versions.map(async (v) => {
            try {
              const data = await listBacktestRuns({ bot_version_id: v.id });
              const list = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
              const sorted = [...list].sort((a, b) => {
                const ta = new Date(a.completed_at || a.created_at || 0).getTime();
                const tb = new Date(b.completed_at || b.created_at || 0).getTime();
                return tb - ta;
              });
              const last = sorted[0] || null;
              return [v.id, last];
            } catch (e) {
              return [v.id, null];
            }
          })
        );
        if (cancelled) return;
        const map = {};
        pairs.forEach(([id, run]) => { map[id] = run; });
        setLastRuns(map);
      } catch (e) {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [versions]);

  // Fetch latest live run per version
  useEffect(() => {
    if (!versions || versions.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const pairs = await Promise.all(
          versions.map(async (v) => {
            try {
              const data = await listLiveRuns({ bot_version_id: v.id });
              const list = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
              const sorted = [...list].sort((a, b) => {
                const ta = new Date(a.started_at || a.created_at || 0).getTime();
                const tb = new Date(b.started_at || b.created_at || 0).getTime();
                return tb - ta; // desc
              });
              const last = sorted[0] || null;
              return [v.id, last];
            } catch (e) {
              return [v.id, null];
            }
          })
        );
        if (cancelled) return;
        const map = {};
        pairs.forEach(([id, run]) => { map[id] = run; });
        setLastLiveRuns(map);
      } catch (e) {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [versions]);

  const handleDeleteBot = async () => {
    if (window.confirm(`Are you sure you want to delete bot "${bot?.name}"? This action cannot be undone.`)) {
      try {
        await deleteBot(botId);
        navigate('/bots');
      } catch (err) {
        console.error("Failed to delete bot:", err);
        setError(err.message || "Failed to delete bot. Please try again.");
      }
    }
  };

  const handleDeleteVersion = async (versionId) => {
    if (window.confirm(`Are you sure you want to delete this version? This action cannot be undone.`)) {
      try {
        await deleteBotVersion(versionId);
        setVersions(versions.filter(v => v.id !== versionId));
      } catch (err) {
        console.error("Failed to delete bot version:", err);
        setError(err.message || "Failed to delete bot version. Please try again.");
      }
    }
  };

  const handleDuplicateVersion = async (versionId) => {
    const newVersionName = prompt("Enter a name for the new duplicated version:");
    if (newVersionName) {
      try {
        const version = await getBotVersion(versionId);
        const payload = {
          version_name: newVersionName,
          strategy_name: version.strategy_name,
          strategy_params: version.strategy_params,
        };
        await createBotVersion(botId, payload);
        const versionsData = await listBotVersions(botId);
        setVersions(versionsData);
      } catch (err) {
        console.error("Failed to duplicate bot version:", err);
        setError(err.message || "Failed to duplicate bot version. Please try again.");
      }
    }
  };

  const openLiveRunModal = (versionId) => {
    setSelectedVersionId(versionId);
    setShowLiveModal(true);
  };

  const openBacktestModal = (versionId) => {
    setSelectedVersionId(versionId);
    setShowBacktestModal(true);
  };

  const handleLiveStarted = () => {
    setShowLiveModal(false);
  };

  const handleStopLiveRun = async (versionId) => {
    const lr = lastLiveRuns[versionId];
    if (!lr || !lr.id) return;
    if (!window.confirm('Stop the running live run?')) return;
    try {
      setStoppingVersionId(versionId);
      await stopLiveRun(lr.id);
      // Refresh latest live run for this version
      try {
        const data = await listLiveRuns({ bot_version_id: versionId });
        const list = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
        const sorted = [...list].sort((a, b) => {
          const ta = new Date(a.started_at || a.created_at || 0).getTime();
          const tb = new Date(b.started_at || b.created_at || 0).getTime();
          return tb - ta;
        });
        const last = sorted[0] || null;
        setLastLiveRuns((prev) => ({ ...prev, [versionId]: last }));
      } catch (_) {
        // ignore
      }
    } catch (e) {
      console.error('Failed to stop live run', e);
      alert(e?.message || 'Failed to stop live run.');
    } finally {
      setStoppingVersionId(null);
    }
  };

  const headerRightContent = bot ? (
    <div className="space-x-2">
      <Link to={`/bots/${botId}/strategy-builder`}>
        <Button variant="secondary">Strategy Builder</Button>
      </Link>
  <Link to={`/bots/${botId}/live-runs`}>
  <Button>Live Runs</Button>
      </Link>
      <Button variant="destructive" onClick={handleDeleteBot}>
        Delete Bot
      </Button>
    </div>
  ) : null;

  if (isLoading) {
    return (
      <PageLayout
        title="Loading Bot Details..."
        backButton={{ label: 'Back to Bots', to: '/bots', preferHistory: false, hideLabelOnMobile: true }}
      >
        <p>Loading...</p>
      </PageLayout>
    );
  }
  if (error) {
    return (
      <PageLayout
        title="Error"
        backButton={{ label: 'Back to Bots', to: '/bots', preferHistory: false, hideLabelOnMobile: true }}
      >
        <p className="text-destructive">{error}</p>
      </PageLayout>
    );
  }
  if (!bot) {
    return (
      <PageLayout
        title="Bot Not Found"
        backButton={{ label: 'Back to Bots', to: '/bots', preferHistory: false, hideLabelOnMobile: true }}
      >
        <p>The requested bot could not be found.</p>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={`Bot: ${bot.name}`}
      headerRightContent={headerRightContent}
      backButton={{ label: 'Back to Bots', to: '/bots', preferHistory: false, hideLabelOnMobile: true }}
    >
      <div className="space-y-6">
        <section>
          <h2 className="text-xl font-semibold text-textPrimary mb-2">Bot Details</h2>
          <div className="bg-backgroundSurface p-4 rounded-lg shadow">
            <p><strong>Strategy Template:</strong> {bot.strategy_template}</p>
            <p><strong>Status:</strong> {bot.is_active ? 'Active' : 'Inactive'}</p>
            <p><strong>Created At:</strong> {new Date(bot.created_at).toLocaleString()}</p>
            <p><strong>Updated At:</strong> {new Date(bot.updated_at).toLocaleString()}</p>
            {/* Display other bot fields as needed */}
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-textPrimary mb-2">Bot Versions</h2>
          {versions.length === 0 ? (
            <p>No versions found for this bot.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {versions.map((version) => (
                <div key={version.id} className="bg-backgroundSurface p-4 rounded-lg shadow border border-borderDefault">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                    {/* Left: Name + Created at */}
                    <div>
                      <div className="text-lg font-semibold text-textPrimary">{version.version_name || 'Unnamed Version'}</div>
                      <div className="text-xs text-textSecondary mt-1">Created: {new Date(version.created_at).toLocaleString()}</div>
                      <div className="text-xs text-textSecondary mt-1">
                        {(() => {
                          const r = lastRuns[version.id];
                          if (!r) return <>Last Backtest: -</>;
                          const dt = new Date(r.completed_at || r.created_at).toLocaleString();
                          let pnlVal = undefined;
                          if (r?.stats?.net_pnl !== undefined && r?.stats?.net_pnl !== null) {
                            const num = typeof r.stats.net_pnl === 'number' ? r.stats.net_pnl : parseFloat(r.stats.net_pnl);
                            pnlVal = isNaN(num) ? undefined : num;
                          }
                          const pnlClass = pnlVal === undefined ? '' : (pnlVal >= 0 ? 'text-green-600' : 'text-red-600');
                          return (
                            <>
                              Last Backtest: {dt} - <span className={pnlClass}>{pnlVal !== undefined ? pnlVal.toFixed(2) : '-'}</span>
                            </>
                          );
                        })()}
                      </div>
                      <div className="text-xs text-textSecondary mt-1">
                        {(() => {
                          const lr = lastLiveRuns[version.id];
                          if (!lr) return <>Last Live Run: -</>;
                          const dt = (lr.started_at || lr.created_at) ? new Date(lr.started_at || lr.created_at).toLocaleString() : '-';
                          // find account friendly name if available
                          const accMap = (accounts || []).reduce((m, a) => { m[a.id] = `${a.name} (${a.platform})`; return m; }, {});
                          const acct = accMap[lr.account_id] || lr.account_id || '-';
                          const instrument = lr.instrument_symbol || lr.symbol || '-';
                          return (
                            <>
                              Last Live Run: {dt} - <StatusBadge status={lr.status || '-'} />
                              <div className="mt-1 text-xs text-textSecondary">Instrument: <span className="font-medium">{instrument}</span> · Account: <span className="font-medium">{acct}</span></div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    {/* Right: Vertical actions */}
                    <div className="flex flex-col gap-2">
                      <Button className="justify-start" onClick={() => openBacktestModal(version.id)}>
                        <Rocket className="w-4 h-4 mr-2" /> Launch Backtest
                      </Button>
                      <Link to={`/bots/${botId}/strategy-builder/${version.id}`} className="w-full">
                        <Button variant="outline" className="w-full justify-start">
                          <Pencil className="w-4 h-4 mr-2" /> Edit Strategy
                        </Button>
                      </Link>
                      <Link to={`/bots/${botId}/versions/${version.id}`} className="w-full">
                        <Button variant="outline" className="w-full justify-start">
                          <Eye className="w-4 h-4 mr-2" /> View Backtest
                        </Button>
                      </Link>
                      {(() => {
                        const status = lastLiveRuns[version.id]?.status;
                        const normalized = String(status || '').trim().toUpperCase();
                        const isRunning = ['RUNNING', 'ACTIVE', 'IN_PROGRESS', 'LIVE'].includes(normalized);
                        if (isRunning) {
                          return (
                            <Button
                              variant="destructive"
                              className="justify-start"
                              onClick={() => handleStopLiveRun(version.id)}
                              disabled={stoppingVersionId === version.id}
                            >
                              <StopCircle className="w-4 h-4 mr-2" /> {stoppingVersionId === version.id ? 'Stopping…' : 'Stop Live Run'}
                            </Button>
                          );
                        }
                        return (
                          <Button variant="secondary" className="justify-start" onClick={() => openLiveRunModal(version.id)}>
                            <PlayCircle className="w-4 h-4 mr-2" /> Start Live Run
                          </Button>
                        );
                      })()}
                      <div className="mt-2 flex items-center gap-2">
                        <Button variant="ghost" size="sm" title="Duplicate" onClick={() => handleDuplicateVersion(version.id)} className="p-2">
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" title="Delete" onClick={() => handleDeleteVersion(version.id)} className="p-2 text-destructive hover:text-destructive">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {showLiveModal && (
          <LaunchLiveRunModal
            botVersionId={selectedVersionId}
            onSuccess={handleLiveStarted}
            onClose={() => setShowLiveModal(false)}
          />
        )}
        {showBacktestModal && (
          <LaunchBacktestModal
            botVersionId={selectedVersionId}
            onSuccess={() => setShowBacktestModal(false)}
            onClose={() => setShowBacktestModal(false)}
          />
        )}
      </div>
    </PageLayout>
  );
};

export default BotDetailPage;

// Modals rendered at the bottom to avoid nesting issues
// Note: We place them outside the main return if needed, but inside the component is fine.
